/**
 * The single door to analytics. Nothing else imports posthog-js directly.
 *
 * Three rules this module exists to enforce:
 *
 *  1. Nothing loads without consent. PostHog sets cookies and records
 *     sessions, so the banner has to actually gate it — before this, the
 *     banner wrote a value to localStorage that nothing ever read, and
 *     "Reject" blocked nothing at all.
 *
 *  2. It is never in the main bundle. posthog-js is ~100 kB gzipped, which
 *     nearly doubled this site's JavaScript. It is loaded with a dynamic
 *     import *after* consent, so visitors who decline — or who never answer —
 *     download none of it. Page speed is part of what this site sells.
 *
 *  3. Callers never have to ask whether tracking is on. `track()` is a silent
 *     no-op without consent, and queues events fired while the library is
 *     still downloading so the first clicks after "Accept" are not lost.
 *
 * With no REACT_APP_POSTHOG_KEY set, every function here does nothing and the
 * site behaves exactly as it did. That is deliberate: the deploy must never
 * depend on analytics being configured.
 */

export const CONSENT_KEY = 'synapsis-cookie-consent';
export const CONSENT_EVENT = 'synapsis:consent-changed';

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

let client = null;
let loading = null;
/** Events fired between "Accept" and the library finishing its download. */
const pending = [];

/** localStorage throws in some privacy modes; never let that break the page. */
function readConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

export function hasConsent() {
  return readConsent() === 'accepted';
}

export function hasDecided() {
  const value = readConsent();
  return value === 'accepted' || value === 'rejected';
}

export function setConsent(decision) {
  try {
    localStorage.setItem(CONSENT_KEY, decision);
  } catch {
    // Can't persist it — still honour the decision for this page view.
  }

  if (decision === 'accepted') {
    startAnalytics();
  } else {
    stopAnalytics();
  }

  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: decision }));
}

export function startAnalytics() {
  if (client || loading || !KEY || !hasConsent()) return loading;

  loading = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(KEY, {
        api_host: HOST,
        // Recordings are the reason this is consent-gated, and the contact
        // form collects names and email addresses — they must never reach a
        // replay.
        session_recording: { maskAllInputs: true },
        mask_all_text: false,
        // Sent manually: this is a single-page app, so automatic capture would
        // only ever see the first route.
        capture_pageview: false,
        autocapture: true,
        persistence: 'localStorage+cookie',
      });

      client = posthog;
      posthog.capture('$pageview');

      while (pending.length) {
        const [event, properties] = pending.shift();
        posthog.capture(event, properties);
      }
    })
    .catch(() => {
      // A blocked or failed download must not break the page, and must not
      // leave the queue growing forever.
      pending.length = 0;
    })
    .finally(() => {
      loading = null;
    });

  return loading;
}

/**
 * Removes what PostHog left on this device.
 *
 * `reset()` clears the stored id but leaves the `ph_<key>_posthog` cookie in
 * place, so declining still left a tracking cookie behind — verified in the
 * browser, which is the only way this shows up. Our own consent key is what
 * remembers the refusal, so PostHog's opt-out cookie is not needed either.
 */
function clearPostHogStorage() {
  if (!KEY) return;
  const names = [`ph_${KEY}_posthog`, `__ph_opt_in_out_${KEY}`];

  names.forEach((name) => {
    // Expire on this host and on the parent domain, since PostHog may have set
    // it on either.
    const parent = `.${window.location.hostname.split('.').slice(-2).join('.')}`;
    [undefined, window.location.hostname, parent].forEach((domain) => {
      document.cookie =
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/` +
        (domain ? `; domain=${domain}` : '');
    });
    try {
      localStorage.removeItem(name);
    } catch {
      // Storage unavailable — the cookie removal above is the part that matters.
    }
  });
}

export function stopAnalytics() {
  pending.length = 0;
  if (client) {
    client.opt_out_capturing();
    client = null;
  }

  // Deliberately no reset(): reset generates a FRESH anonymous id and writes
  // it back, which is why declining used to leave a tracking cookie behind
  // even though the code looked like it was clearing one. Starting a new
  // identity is meaningless when the point is to stop.
  clearPostHogStorage();

  // PostHog persists on a later tick, so a single synchronous sweep can be
  // undone by a write already in flight. Verified in the browser: without this
  // second pass the cookie survives.
  setTimeout(clearPostHogStorage, 0);
}

export function capturePageview() {
  if (!client) return;
  client.capture('$pageview');
}

/**
 * Fire an event. Safe to call anywhere: does nothing without consent.
 *
 * Event names and properties are a contract — renaming one orphans its
 * history — so they live in ANALYTICS_EVENTS rather than as loose strings
 * scattered through components.
 */
export function track(event, properties) {
  if (client) {
    client.capture(event, properties);
    return;
  }
  // Consent given but the library still downloading: hold the event rather
  // than drop it. Bounded so a failure cannot leak memory.
  if (loading && pending.length < 50) {
    pending.push([event, properties]);
  }
}

export const ANALYTICS_EVENTS = {
  CTA_CLICKED: 'cta_clicked',
  WHATSAPP_CLICKED: 'whatsapp_clicked',
  CONTACT_STARTED: 'contact_started',
  CONTACT_SUBMITTED: 'contact_submitted',
  CONTACT_FAILED: 'contact_failed',
  PRICING_VIEWED: 'pricing_viewed',
  LANGUAGE_SWITCHED: 'language_switched',
  FAQ_OPENED: 'faq_opened',
  WEB_VITAL: 'web_vital',
};
